import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

import ActionButton from '../../lib/ActionButton';
import { formatBytes } from '../../lib/datasetUI';

/**
 * Pretty-prints an MQTT status object into a key/value list.
 *
 * Extracted from AdminDatasetRow so the data-display logic is testable
 * in isolation and the row component stays focused on layout. Also lays
 * the foundation for a generic <DefinitionList> primitive — Inspector's
 * `Metadata` component renders the same `<b>label:</b> value` pattern by
 * hand and could share this in the future.
 *
 * Self-signed brokers expose an extra row with a "Download .pem" button
 * that hits GET /v1/dataset/:datasetid/mqtt/cert. The same endpoint
 * returns 404 for Let's Encrypt brokers, so we only render the button
 * when status.config.ingress.certType === 'selfsigned' — and surface a
 * muted reassurance line in the LE case so users don't wonder where
 * the cert is.
 */

// API base URL is read the same way apiSlice.js does — single source of
// truth for the runtime-injected env var that lets the same bundle be
// deployed to dev / staging / prod without rebuilding.
const apiBaseUrl = window._env_?.REACT_APP_WCIBAPI_URL || '';

// Field labels exposed to non-admin viewers. Operational details
// (helm release, pod statuses, broker version, retention, ingester
// internals, log levels) are admin-only — they're noise to a user
// who just wants to know "where do I connect, and how". Order is
// preserved by filtering the existing `rows` array, so this set is
// just a membership test, not a re-ordering.
//
// 'Broker certificate' is rendered separately (its own component
// branch with the download button), so it's NOT in this set; it
// always shows in both modes when certType is selfsigned.
const USER_VISIBLE_LABELS = new Set([
    'Status',
    'Ingress hostname',
    'Cert type',
    'Token expiry'
]);

/**
 * Pod status objects come back from the k8s client as
 *   { found, name, ready, restarts, phase, startTime }
 * (see wcibapi/libs/k8sClient.js → getPodStatus). There is no `.status`
 * field — the previous fallback `pod?.status || 'unknown'` always missed
 * and rendered "unknown" even when the pod was happily Running. Derive a
 * single human-readable string from the fields that actually exist.
 */
function podStatusLabel(pod) {
    if (!pod) return 'unknown';
    if (pod.found === false) return 'not found';
    const phase = pod.phase || 'unknown';
    if (pod.ready === false && phase === 'Running') return `${phase} (not ready)`;
    if (pod.restarts > 0) return `${phase} (${pod.restarts} restart${pod.restarts === 1 ? '' : 's'})`;
    return phase;
}

// Single row primitive matching the visual rhythm of the rest of the
// list. Extracted so the cert-download row stays consistent with the
// auto-generated key/value rows above it.
function InfoRow({ label, children }) {
    return (
        <Typography variant="body2" component="div"
            sx={{
                display: 'flex',
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { sm: 'center' }
            }}>
            <Box component="span" sx={{ minWidth: 170, fontWeight: 600 }}>{label}:</Box>
            <Box component="span" sx={{ wordBreak: 'break-word' }}>{children}</Box>
        </Typography>
    );
}

InfoRow.propTypes = {
    label: PropTypes.node.isRequired,
    children: PropTypes.node
};

export default function MqttInfo({ status, datasetId, mode = 'admin' }) {
    if (!status) return null;
    const cfg = status.config || {};
    // `helm status -o json` puts the lifecycle string under `info.status`
    // ("deployed", "failed", ...). Older / error paths in helmClient.js
    // return a flat { status: 'not_found' | 'unknown' }, so honour both.
    const helmStatus = status.helm?.info?.status || status.helm?.status || 'unknown';
    const mosquittoPodStatus = podStatusLabel(status.mosquitto);
    const ingesterPodStatus = podStatusLabel(status.ingester);
    const expiry = status.tokenExpiryDate
        ? new Date(status.tokenExpiryDate).toLocaleDateString()
        : 'n/a';

    const certType = cfg.ingress?.certType;
    const isSelfSigned = certType === 'selfsigned';
    const certHref = `${apiBaseUrl}/v1/dataset/${datasetId}/mqtt/cert`;
    const certFilename = `mqtt-ds${datasetId}.pem`;

    const rows = [
        ['Status', status.status || 'unknown'],
        ['Helm release', helmStatus],
        ['Mosquitto pod', mosquittoPodStatus],
        ['Ingester pod', ingesterPodStatus],
        ['Mosquitto version', cfg.mqtt?.version],
        ['Retention', cfg.mqtt?.retention !== undefined ? `${cfg.mqtt.retention}s` : undefined],
        ['Broker log level', cfg.mqtt?.logLevel],
        ['Ingester enabled', typeof cfg.ingester?.enabled === 'boolean' ? (cfg.ingester.enabled ? 'yes' : 'no') : undefined],
        ['Filename prefix', cfg.ingester?.filenamePrefix],
        ['Data flag', cfg.ingester?.dataFlag],
        ['Max duration', cfg.ingester?.maxDuration !== undefined ? `${cfg.ingester.maxDuration}s` : undefined],
        ['Max size', cfg.ingester?.maxSize !== undefined ? formatBytes(cfg.ingester.maxSize) : undefined],
        ['Ingester log level', cfg.ingester?.logLevel],
        ['Ingress enabled', typeof cfg.ingress?.enabled === 'boolean' ? (cfg.ingress.enabled ? 'yes' : 'no') : undefined],
        ['Ingress hostname', cfg.ingress?.hostname],
        ['Cert type', certType],
        ['Token expiry', expiry]
    ].filter(([, v]) => v !== undefined && v !== null && v !== '');

    // User-mode whitelist — drop the operational rows. The cert row
    // below is rendered unconditionally regardless of mode (when
    // applicable), since downloading the broker cert is part of "how
    // do I connect", which user mode IS for.
    const visibleRows = mode === 'user'
        ? rows.filter(([label]) => USER_VISIBLE_LABELS.has(label))
        : rows;

    return (
        <Box>
            {visibleRows.map(([k, v]) => (
                <InfoRow key={k} label={k}>{String(v)}</InfoRow>
            ))}
            {isSelfSigned && (
                <InfoRow label="Broker certificate">
                    {/*
                      Anchor-style ActionButton: the browser's native
                      `download` attribute handles the file save, no
                      JS fetch + Blob round-trip needed. Same-origin
                      session cookie tags along automatically — the
                      backend gates this endpoint on the same access
                      role as /mqtt/status.
                    */}
                    <ActionButton
                        component="a"
                        href={certHref}
                        download={certFilename}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                    >
                        Download .pem
                    </ActionButton>
                </InfoRow>
            )}
            {certType && !isSelfSigned && (
                // LE brokers chain to a publicly trusted root — no
                // ca_certs needed in the client. State this
                // explicitly so users don't go looking for a download
                // affordance that intentionally isn't there.
                <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', mt: 0.5, ml: { sm: '170px' } }}>
                    Trusted by your system store — no extra setup needed.
                </Typography>
            )}
        </Box>
    );
}

MqttInfo.propTypes = {
    status: PropTypes.object,
    // Required so the cert-download href can be built. We don't fall
    // back to status.* for this — keeping the dataset id explicit at
    // the prop boundary documents that the caller (AdminDatasetRow)
    // already has it from ds.DatasetID.
    datasetId: PropTypes.number.isRequired,
    // 'admin' (default) shows every row; 'user' applies the
    // USER_VISIBLE_LABELS whitelist so non-admin viewers see only the
    // fields they need to connect (status, hostname, cert type, token
    // expiry, plus the cert download row when applicable).
    mode: PropTypes.oneOf(['admin', 'user'])
};
